import { prenatalScopeFrom } from './prenatalScope';
import React, { useContext } from 'react';
import Grid from '@mui/material/Grid';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS, HPO_CONSTANTS } from 'src/components/Constants';
import moment from 'moment';

import axios from 'src/axios';

import {
  Box,
  FormControl,
  TextField,
  Switch,
  Typography,
  IconButton,
  Button,
  Tooltip,
  InputAdornment
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  getDependencies,
  VARIANT_INTERPRETATION_OPTIONS,
  VARIANT_ORIGIN_OPTIONS,
  getConsentTags,
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault
} from './formService';

import { calculateAge, getHPOSearchURL } from 'src/util/util';
import {
  readTerminologyAvailability,
  terminologyAvailabilityFromError
} from 'src/util/terminologyAvailability';
import Alert from '@mui/material/Alert';
import {
  SnackContext,
  AppContext,
  ThemeContext,
  UserContext
} from 'src/store/ContextStore';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import TextInput from 'src/components/UI/input/TextInput';

import UploadFileList from 'src/components/UI/input/UploadFileList';
import SelectInput from 'src/components/UI/input/SelectInput';
import CheckboxInput from 'src/components/UI/input/CheckboxInput';
import InfoTooltip from 'src/components/UI/addons/InfoTooltip';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import AssociatedNote from 'src/components/UI/input/AssociatedNote';
import SavedVariantTile from './SavedVariantTile';
import { useClinicalSuggestions } from 'src/hooks/useClinicalSuggestions';
import ClinicalSuggestionsPanel from 'src/components/UI/ClinicalSuggestionsPanel';
import { pushVariantsToGenotype } from '../../genotypes-menu/pushVariantToGenotype';

export default function TestForm({
  data,
  handleClose,
  familyMemberId,
  isProband,
  isEditing,
  model,
  staffMembers,
  staffMemberOptions,
  setUnsavedChanges,
  refreshPedigreeLabels
}) {
  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { theme } = useContext(ThemeContext);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { user: loggedInUser } = React.useContext(UserContext);
  const [geneSearchTerm, setGeneSearchTerm] = React.useState('');
  const [geneOptions, setGeneOptions] = React.useState([]);
  const [geneUnavailable, setGeneUnavailable] = React.useState(null);
  const [testNameOptions, setTestNameOptions] = React.useState([]);
  const [testTypeOptions, setTestTypeOptions] = React.useState([]);
  const [labOptions, setLabOptions] = React.useState([]);
  const [testResultOptions, setTestResultOptions] = React.useState([]);

  const isBillableValid = React.useMemo(
    () => !!data?.firstName && !!data?.lastName,
    [data]
  );

  const patientDob = data?.dob;
  const computeAgeWhenTested = React.useCallback(
    (dateRequested) => {
      if (!patientDob || !dateRequested) return '';
      const parsedDate = new Date(dateRequested);
      if (Number.isNaN(parsedDate.getTime())) return '';
      return calculateAge(patientDob, parsedDate);
    },
    [patientDob]
  );

  const defaultDateRequested = new Date();
  const defaultValues = isEditing
    ? {
        ...model,
        requestedBy: Array.isArray(model.requestedBy)
          ? model.requestedBy.map((r) => r.userId || r._id).filter(Boolean)
          : []
      }
    : {
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        // PRENATAL SCOPE (decision 21) — see prenatalScope.js for why every prenatal-capable form
        // must spread this. Empty for an ordinary test.
        ...prenatalScopeFrom(model),
        testName: '',
        testType: '',
        lab: '',
        billable: false,
        dateRequested: defaultDateRequested,
        dateResultDue: null,
        dateResultReceived: null,
        ageWhenTested: computeAgeWhenTested(defaultDateRequested),
        testResult: '',
        testNotes: '',
        fileAttachments: [],
        showInFamily: false,
        variants: [],
        associatedConsent: { label: 'none', _id: '' },
        requestedBy: loggedInUser._id ? [loggedInUser._id] : [],
        reminderEnabled: false,
        reminderDate: null
      };

  const [formValues, setFormValues] = React.useState(defaultValues);

  const allStaffMembers = React.useMemo(() => {
    return staffMemberOptions.map((options) => options.value);
  }, [staffMemberOptions]);

  const staffMembersMap = React.useMemo(() => {
    return staffMemberOptions.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, [staffMemberOptions]);

  const emptyVariant = {
    gene: '',
    variantG: '',
    variantC: '',
    variantP: '',
    variantInterpretation: '',
    origin: 'unknown',
    dateLastReviewed: null
  };
  const [currentVariant, setCurrentVariant] = React.useState(emptyVariant);
  const [editingVariantIndex, setEditingVariantIndex] = React.useState(null);
  const variantEditorRef = React.useRef(null);
  const isEditingVariant = editingVariantIndex !== null;

  const handleAddVariant = () => {
    if (!currentVariant.gene) return;
    setFormValues((prev) => ({
      ...prev,
      variants: [...(prev.variants || []), { ...currentVariant }]
    }));
    setCurrentVariant(emptyVariant);
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const handleUpdateVariant = () => {
    if (editingVariantIndex === null) return;
    if (!currentVariant.gene) return;
    setFormValues((prev) => ({
      ...prev,
      variants: (prev.variants || []).map((v, i) =>
        i === editingVariantIndex ? { ...currentVariant } : v
      )
    }));
    setCurrentVariant(emptyVariant);
    setEditingVariantIndex(null);
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const handleEditVariant = (index) => {
    const target = formValues.variants?.[index];
    if (!target) return;
    setCurrentVariant({ ...emptyVariant, ...target });
    setEditingVariantIndex(index);
    requestAnimationFrame(() => {
      variantEditorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  };

  const handleCancelEditVariant = () => {
    setCurrentVariant(emptyVariant);
    setEditingVariantIndex(null);
  };

  const handleDeleteVariant = (index) => {
    setFormValues((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
    if (editingVariantIndex === index) {
      setCurrentVariant(emptyVariant);
      setEditingVariantIndex(null);
    } else if (editingVariantIndex !== null && index < editingVariantIndex) {
      setEditingVariantIndex(editingVariantIndex - 1);
    }
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  React.useEffect(() => {
    if (geneSearchTerm.length < 3) {
      setGeneOptions([]);
      return;
    }
    const url = getHPOSearchURL(
      geneSearchTerm,
      HPO_CONSTANTS.initialPage,
      HPO_CONSTANTS.category['gene']
    );
    axios
      .get(url)
      .then(({ data }) => {
        // The catch below used to swallow every failure into an empty list
        // without even binding the error, so an outage, an admin-disabled
        // HGNC and an empty vocabulary were one indistinguishable empty
        // dropdown on the variant row (TGCLOUD-1785).
        setGeneUnavailable(readTerminologyAvailability(data));
        const { results = [] } = data || {};
        setGeneOptions(results.map((d) => ({ id: d.id, name: d.name })));
      })
      .catch((err) => {
        setGeneUnavailable(terminologyAvailabilityFromError());
        setGeneOptions([]);
        console.error('Error fetching genes:', err);
      });
  }, [geneSearchTerm]);

  React.useEffect(() => {
    getClinicalDefaults('testNames').then(setTestNameOptions);
    getClinicalDefaults('testTypes').then(setTestTypeOptions);
    getClinicalDefaults('laboratories').then(setLabOptions);
    getClinicalDefaults('testResults').then(setTestResultOptions);
  }, []);

  const [reminderTouched, setReminderTouched] = React.useState(false);
  const computeDefaultReminderDate = React.useCallback((reqDate) => {
    if (!reqDate) return null;
    return moment(reqDate).add(14, 'days').toDate();
  }, []);

  let [consentOptions, setConsentOptions] = React.useState(['loading...']);
  React.useEffect(() => {
    const fetchData = async () => {
      const consentTags = await getConsentTags(
        theme,
        familyMemberId,
        formValues.familyId
      );
      setConsentOptions(consentTags);
    };
    if (consentOptions != [] && consentOptions[0] == 'loading...') fetchData();
  }, []);

  const [error, setError] = React.useState({});

  const clearEitherDateErrors = () => {
    setError((prev) => {
      const next = { ...prev };
      if (typeof next.dateRequested === 'string') delete next.dateRequested;
      if (typeof next.dateResultReceived === 'string')
        delete next.dateResultReceived;
      return next;
    });
  };

  const handleChange = (e) => {
    if (e.target.name === 'dateRequested') {
      const nextReq = e.target.value;
      if (nextReq) clearEitherDateErrors();
      if (!formValues.reminderEnabled || !reminderTouched) {
        const nextDefault = computeDefaultReminderDate(nextReq);
        setFormValues((prev) => ({ ...prev, reminderDate: nextDefault }));
      }
      setFormValues((prevValues) => ({
        ...prevValues,
        ageWhenTested: computeAgeWhenTested(nextReq)
      }));
    }

    if (e.target.type === 'date') {
      const selectedDate = new Date(e.target.value);
      const today = new Date();

      if (selectedDate >= today) {
        setError((prev) => ({ ...prev, [e.target.name]: true }));
      } else {
        setError((prev) => {
          const { [e.target.name]: _, ...rest } = prev;
          return rest;
        });
      }
    }
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const onToggleSwitch = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.checked
    }));
    if (familyMemberId == 'none') {
      setFormValues((prevValues) => ({
        ...prevValues,
        showInFamily: true
      }));
    }
  };

  const { setSnack } = React.useContext(SnackContext);

  const {
    suggestions,
    analyzingNote,
    selectedSuggestions,
    savingNote,
    handleTextBlur,
    handleAttachmentText,
    toggleSuggestion,
    acceptSuggestions,
    analyzeText
  } = useClinicalSuggestions({ familyMemberId, isProband });

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeWhenTested(formValues.dateRequested);
    setFormValues((prev) => {
      const currentAge =
        prev.ageWhenTested === undefined ? '' : prev.ageWhenTested;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageWhenTested: nextAge };
    });
  }, [patientDob, computeAgeWhenTested, formValues.dateRequested]);

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async (e) => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: `Associated record exists; check the ${dep[0]}`,
        severity: 'error'
      });
      setLoading(false);

      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then(async () => {
        await refreshPedigreeLabels?.();
        setSnack({
          status: true,
          msg: 'Record deleted',
          severity: 'success'
        });
        handleCloseModal();
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Delete failed! ${err}`,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const isDateDueValid = () =>
    !formValues.dateRequested ||
    !formValues.dateResultDue ||
    moment(formValues.dateRequested) <=
      moment(formValues.dateResultDue).add('1', 'day');

  const isDateResultReceivedValid = () =>
    !formValues.dateRequested ||
    !formValues.dateResultReceived ||
    moment(formValues.dateRequested) <=
      moment(formValues.dateResultReceived).add('1', 'day');

  const validateForm = (values) => {
    const errs = {};

    if (!values.testName?.trim()) {
      errs.testName = 'Test Name is required';
    }

    if (!values.dateRequested && !values.dateResultReceived) {
      errs.dateRequested =
        'Either Date Requested or Date Result Received is required';
      errs.dateResultReceived =
        'Either Date Requested or Date Result Received is required';
    }

    if (values.testResult?.trim() && !values.dateResultReceived) {
      errs.dateResultReceived = 'Required when Test Result is provided';
    }

    if (!isDateDueValid()) {
      errs.dateResultDue = 'Test cannot be due before it was requested';
    }

    if (!isDateResultReceivedValid()) {
      errs.dateResultReceived =
        errs.dateResultReceived ||
        "Test can't be received before being requested";
    }

    setError((prev) => {
      const futureDateErrors = Object.fromEntries(
        Object.entries(prev).filter(([, v]) => v === true)
      );
      return { ...futureDateErrors, ...errs };
    });
    return errs;
  };

  // After a test is saved, ensure a genotype record exists for each gene
  // referenced by the test's variants and append the variants there. The
  // genotype endpoint dedupes by (gene, variantG, variantC, variantP) so
  // re-saves don't create duplicates.
  const syncTestVariantsToGenotypes = async (payload, savedTest) => {
    const userId = user?.user_id || user?._id;
    if (!userId || !familyMemberId) return;

    const allVariants = Array.isArray(payload?.variants)
      ? payload.variants
      : [];
    const variantsByGene = new Map();
    allVariants.forEach((v) => {
      const gene = (v?.gene || '').trim();
      if (!gene) return;
      if (!variantsByGene.has(gene)) variantsByGene.set(gene, []);
      variantsByGene.get(gene).push(v);
    });
    if (variantsByGene.size === 0) return;

    const sourceTestId = savedTest?._id || payload?._id;
    const sourceTestName = payload?.testName;
    const sourceTestDate =
      payload?.dateResultReceived || payload?.dateRequested || null;

    const buildVariantPayload = (gene, variants) =>
      variants
        .map((v) => ({
          gene,
          variantG: v.variantG || '',
          variantC: v.variantC || '',
          variantP: v.variantP || '',
          labInterpretation: v.variantInterpretation || '',
          // Inherited vs acquired. Absent means 'unknown', which the
          // analyzer treats as potentially heritable.
          origin: v.origin || 'unknown',
          sourceTestId,
          sourceTestName,
          sourceTestDate
        }))
        .filter(
          (v) => v.variantG || v.variantC || v.variantP || v.labInterpretation
        );

    await Promise.all(
      Array.from(variantsByGene.entries()).map(([gene, variants]) =>
        pushVariantsToGenotype({
          user_id: userId,
          familyMemberId,
          isProband,
          gene,
          name: gene,
          variants: buildVariantPayload(gene, variants)
        }).catch(() => {
          /* best-effort — don't block test save success */
        })
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const hasFutureDateErrors = Object.values(error).some((v) => v === true);
    const validationErrors = validateForm(formValues);
    if (Object.keys(validationErrors).length > 0 || hasFutureDateErrors) {
      document.getElementById('modaltop')?.scrollIntoView();
      return Promise.resolve();
    }
    const requestedByPayload =
      formValues.requestedBy && formValues.requestedBy.length > 0
        ? formValues.requestedBy
            .map((id) => staffMembers.find((member) => member.userId === id))
            .filter(Boolean)
        : [];

    const editorHasContent =
      !!currentVariant.gene ||
      !!currentVariant.variantG ||
      !!currentVariant.variantC ||
      !!currentVariant.variantP ||
      !!currentVariant.variantInterpretation ||
      !!currentVariant.dateLastReviewed;

    let mergedVariants = formValues.variants || [];
    if (isEditingVariant && editorHasContent) {
      mergedVariants = mergedVariants.map((v, i) =>
        i === editingVariantIndex ? { ...currentVariant } : v
      );
    } else if (!isEditingVariant && editorHasContent) {
      mergedVariants = [...mergedVariants, { ...currentVariant }];
    }

    const parsePayload = {
      ...formValues,
      variants: mergedVariants,
      requestedBy: requestedByPayload
    };

    return axios
      .put(APICONSTANTS.test, parsePayload)
      .then(async (res) => {
        if (res.status === 200) {
          await syncTestVariantsToGenotypes(parsePayload, res.data);
          await refreshPedigreeLabels?.();
          handleClose({ exit: true });
          setSnack({
            status: true,
            msg: 'Successfully saved',
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: `Upload failed!`,
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Upload failed! ${err}`,
          severity: 'error'
        });
      });
  };

  return (
    //Should be fine to reuse old class?

    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Test ?"
        content="Warning: This action will permanently delete this Test. Once deleted, this
        information will be permanently removed and cannot be restored."
      />
      <Grid container spacing={2}>
        <Grid id="modaltop" size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel required>Test Name</FormLabel>
          </Grid>
          <AutocompleteInput
            name="testName"
            value={formValues.testName || ''}
            options={testNameOptions}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'testName', value: label } });
              if (label) {
                setError((prev) => {
                  const { testName: _, ...rest } = prev;
                  return rest;
                });
                saveClinicalDefault('testNames', label).then(
                  setTestNameOptions
                );
              }
            }}
            onDelete={(option) =>
              deleteClinicalDefault('testNames', option.label).then(
                setTestNameOptions
              )
            }
            highlightErrorBorder={!!error.testName}
            helperText={error.testName}
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Reason for Test</FormLabel>
          </Grid>
          <AutocompleteInput
            name="testType"
            value={formValues.testType || ''}
            options={testTypeOptions}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'testType', value: label } });
              if (label)
                saveClinicalDefault('testTypes', label).then(
                  setTestTypeOptions
                );
            }}
            onDelete={(option) =>
              deleteClinicalDefault('testTypes', option.label).then(
                setTestTypeOptions
              )
            }
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Date Requested</FormLabel>
          </Grid>
          <DateInput
            name="dateRequested"
            value={formValues.dateRequested}
            disableFuture
            onChange={(val) =>
              handleChange({
                target: { name: 'dateRequested', value: val, type: 'date' }
              })
            }
            variant="outline"
            error={!!error.dateRequested}
            helperText={
              typeof error.dateRequested === 'string'
                ? error.dateRequested
                : 'Date cannot be in the future'
            }
          />
        </Grid>
        <Grid size={6}>
          <TextInput
            label="Age When Tested"
            name="ageWhenTested"
            type="number"
            value={formValues.ageWhenTested ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND date requested both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.dateRequested}
            onChange={
              patientDob && formValues.dateRequested
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageWhenTested: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Associated Consent</FormLabel>
          </Grid>
          <AssociatedNote
            name="associatedConsent"
            handleChange={handleChange}
            value={formValues.associatedConsent}
            noteOptions={consentOptions}
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Result Due Date</FormLabel>
          </Grid>
          <DateInput
            value={formValues.dateResultDue || null}
            onChange={(e) =>
              setFormValues((prevValues) => ({
                ...prevValues,
                dateResultDue: e
              }))
            }
            variant="outline"
            minDate={formValues.dateRequested}
            error={!!error.dateResultDue}
            helperText={error.dateResultDue || ''}
            helperTextColour="red"
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Date Result Received</FormLabel>
          </Grid>
          <DateInput
            value={formValues.dateResultReceived}
            onChange={(e) => {
              if (e) clearEitherDateErrors();
              setFormValues((prevValues) => ({
                ...prevValues,
                dateResultReceived: e
              }));
            }}
            variant="outline"
            disableFuture
            minDate={formValues.dateRequested}
            error={!!error.dateResultReceived}
            helperText={error.dateResultReceived || ''}
            helperTextColour="red"
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Laboratory</FormLabel>
          </Grid>
          <AutocompleteInput
            name="lab"
            value={formValues.lab || ''}
            options={labOptions}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'lab', value: label } });
              if (label)
                saveClinicalDefault('laboratories', label).then(setLabOptions);
            }}
            onDelete={(option) =>
              deleteClinicalDefault('laboratories', option.label).then(
                setLabOptions
              )
            }
          />
        </Grid>
        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Test Billable</FormLabel>
          </Grid>
          <CheckboxInput
            disabled={!isBillableValid}
            value={formValues.billable}
            label={formValues.billable ? 'Yes' : 'No'}
            name={'billable'}
            onChange={handleChange}
          />
          {!isBillableValid && (
            <InfoTooltip label={'Firstname & Lastname required'} />
          )}
        </Grid>

        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Test Result</FormLabel>
          </Grid>
          <AutocompleteInput
            name="testResult"
            value={formValues.testResult || ''}
            options={testResultOptions}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'testResult', value: label } });
              if (label)
                saveClinicalDefault('testResults', label).then(
                  setTestResultOptions
                );
            }}
            onDelete={(option) =>
              deleteClinicalDefault('testResults', option.label).then(
                setTestResultOptions
              )
            }
          />
        </Grid>

        <Grid ref={variantEditorRef} size={12}>
          {isEditingVariant && (
            <Box
              sx={{
                mb: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 1,
                backgroundColor: 'rgba(124, 77, 255, 0.08)',
                border: '1px solid rgba(124, 77, 255, 0.35)',
                fontSize: 13
              }}
            >
              Editing saved variant — make changes below, then click{' '}
              <strong>Update variant</strong>.
            </Box>
          )}
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Gene</FormLabel>
          </Grid>

          {geneUnavailable && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {geneUnavailable.message}
            </Alert>
          )}

          <Autocomplete
            clearOnBlur={false}
            options={geneOptions}
            noOptionsText={
              geneUnavailable
                ? 'Gene lookup unavailable — this is not a result'
                : geneSearchTerm.length < 3
                ? 'Type at least 3 characters'
                : 'No matching genes'
            }
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            inputValue={currentVariant.gene}
            onInputChange={(_, value) => {
              setGeneSearchTerm(value);
              setCurrentVariant((prev) => ({ ...prev, gene: value }));
            }}
            onChange={(_, value) => {
              const name =
                value && typeof value === 'object' ? value.name : value || '';
              setGeneSearchTerm('');
              setCurrentVariant((prev) => ({ ...prev, gene: name }));
            }}
            filterOptions={(options) => options}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                label="Search for Genes"
                placeholder="Search..."
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <FormLabel>Variant Details</FormLabel>
        </Grid>

        <Grid size={4}>
          <TextField
            variant="outlined"
            fullWidth
            label="g."
            value={currentVariant.variantG}
            onChange={(e) =>
              setCurrentVariant((prev) => ({
                ...prev,
                variantG: e.target.value
              }))
            }
            sx={{
              '& label.Mui-focused': { color: 'grey' }
            }}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            variant="outlined"
            fullWidth
            label="c."
            value={currentVariant.variantC}
            onChange={(e) =>
              setCurrentVariant((prev) => ({
                ...prev,
                variantC: e.target.value
              }))
            }
            sx={{
              '& label.Mui-focused': { color: 'grey' }
            }}
          />
        </Grid>

        <Grid size={4}>
          <TextField
            variant="outlined"
            fullWidth
            label="p."
            value={currentVariant.variantP}
            onChange={(e) =>
              setCurrentVariant((prev) => ({
                ...prev,
                variantP: e.target.value
              }))
            }
            sx={{
              '& label.Mui-focused': { color: 'grey' }
            }}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Variant Interpretation</FormLabel>
          </Grid>
          <SelectInput
            fullWidth={true}
            variant="outlined"
            name="variantInterpretation"
            options={VARIANT_INTERPRETATION_OPTIONS}
            value={currentVariant.variantInterpretation}
            onChange={(e) =>
              setCurrentVariant((prev) => ({
                ...prev,
                variantInterpretation: e.target.value
              }))
            }
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Variant Origin</FormLabel>
          </Grid>
          <SelectInput
            fullWidth={true}
            variant="outlined"
            name="origin"
            options={VARIANT_ORIGIN_OPTIONS}
            value={currentVariant.origin || 'unknown'}
            onChange={(e) =>
              setCurrentVariant((prev) => ({
                ...prev,
                origin: e.target.value
              }))
            }
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Date Last Reviewed</FormLabel>
          </Grid>
          <DateInput
            value={currentVariant.dateLastReviewed}
            onChange={(val) =>
              setCurrentVariant((prev) => ({ ...prev, dateLastReviewed: val }))
            }
            variant="outline"
          />
        </Grid>

        <Grid
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
          size={12}
        >
          {isEditingVariant && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelEditVariant}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={isEditingVariant ? handleUpdateVariant : handleAddVariant}
          >
            {isEditingVariant ? 'Update variant' : 'Add another variant'}
          </Button>
        </Grid>

        {formValues.variants?.length > 0 && (
          <Grid size={12}>
            <Box sx={{ paddingBottom: 1 }}>
              <FormLabel>Saved Variants</FormLabel>
            </Box>
            {formValues.variants.map((v, i) => (
              <SavedVariantTile
                key={i}
                variant={v}
                onEdit={() => handleEditVariant(i)}
                onDelete={() => handleDeleteVariant(i)}
                isEditing={editingVariantIndex === i}
              />
            ))}
          </Grid>
        )}

        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Test Notes</FormLabel>
          </Grid>
          <Box sx={{ width: '100%' }}>
            <TextField
              variant="outlined"
              fullWidth
              onChange={handleChange}
              name="testNotes"
              value={formValues.testNotes}
              multiline={true}
              slotProps={{
                input: {
                  endAdornment: formValues.testNotes ? (
                    <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                      <Tooltip title="Analyse for clinical terms">
                        <span style={{ lineHeight: 0 }}>
                          <IconButton
                            size="medium"
                            onClick={() =>
                              analyzeText(formValues.testNotes, 'manual')
                            }
                            disabled={analyzingNote}
                          >
                            <AutoAwesomeIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ) : null
                }
              }}
            />
          </Box>
        </Grid>

        <ClinicalSuggestionsPanel
          suggestions={suggestions}
          analyzingNote={analyzingNote}
          selectedSuggestions={selectedSuggestions}
          toggleSuggestion={toggleSuggestion}
          onAccept={() =>
            acceptSuggestions().then((result) => {
              if (result?.success) {
                const { count = {} } = result;
                const parts = [];
                if (count.phenotypes)
                  parts.push(`${count.phenotypes} phenotype(s)`);
                if (count.genotypes)
                  parts.push(`${count.genotypes} genotype(s)`);
                if (count.diseases) parts.push(`${count.diseases} disease(s)`);
                setSnack({
                  status: true,
                  msg: `Added ${parts.join(', ')} to patient record`,
                  severity: 'success'
                });
              } else {
                setSnack({
                  status: true,
                  msg: 'Failed to save suggestions',
                  severity: 'error'
                });
              }
            })
          }
          savingNote={savingNote}
        />

        <UploadFileList
          value={formValues.fileAttachments}
          familyId={familyMemberId}
          onChange={(value) =>
            setFormValues({
              ...formValues,
              fileAttachments: value
            })
          }
          onUploadComplete={(uploadData) => {
            const text = uploadData?.extractedText;
            if (text) handleAttachmentText(text);
          }}
        />

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Show in family
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.showInFamily ?? false}
                onChange={onToggleSwitch}
                name="showInFamily"
              />
            </Grid>
            <Grid>
              <Typography>{formValues.showInFamily ? 'Yes' : 'No'}</Typography>
            </Grid>
          </Grid>
        </Grid>

        {/* RequestedBy */}
        <Grid size={12}>
          <FormControl component="fieldset" fullWidth>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextInput
                  label={'Requested By'}
                  name="requestedBy"
                  type="multiple-select"
                  value={formValues?.requestedBy || []}
                  onChange={handleChange}
                  options={allStaffMembers || []}
                  mapper={staffMembersMap || {}}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>

        <Grid size={12}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 'auto'
              }}
            >
              <FormLabel>Reminder</FormLabel>
            </Grid>
            <Grid>
              <Switch
                checked={!!formValues.reminderEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormValues((prev) => {
                    const next = { ...prev, reminderEnabled: enabled };
                    if (enabled && !prev.reminderDate) {
                      next.reminderDate = computeDefaultReminderDate(
                        prev.dateRequested
                      );
                    }
                    return next;
                  });
                  setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                }}
                name="reminderEnabled"
                color="secondary"
              />
            </Grid>
            {formValues.reminderEnabled && (
              <Grid
                sx={{ marginLeft: 14 }}
                size={{
                  xs: 12,
                  sm: 6
                }}
              >
                <Grid sx={{ paddingBottom: 1 }}>
                  <FormLabel>Reminder Date</FormLabel>
                </Grid>
                <DateInput
                  name="reminderDate"
                  value={formValues.reminderDate}
                  disablePast
                  minDate={formValues.dateRequested || new Date()}
                  onChange={(val) => {
                    setReminderTouched(true);
                    handleChange({
                      target: { name: 'reminderDate', value: val, type: 'date' }
                    });
                  }}
                  variant="outline"
                  helperText={'Default is 2 weeks from Date Requested'}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <ModalButtons
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing ? handleOpenModal : null}
          handleClose={handleClose}
          addLabel={`Save New Test`}
        />
      </Grid>
    </form>
  );
}
